/**
 * Formateo para lectura humana. Todo en horario de Chile continental, que es el
 * huso con el que opera el negocio: si el navegador esta en otra zona, las horas
 * igual se leen como las ve la operacion.
 */

const TIME_ZONE = 'America/Santiago';

const dateTimeFormatter = new Intl.DateTimeFormat('es-CL', {
  timeZone: TIME_ZONE,
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
});

const timeFormatter = new Intl.DateTimeFormat('es-CL', {
  timeZone: TIME_ZONE,
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
});

/** `26-08-2026 18:31:05`. Devuelve un guion si no hay fecha. */
export function formatDateTime(value: string | null | undefined): string {
  if (!value) {
    return '—';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return dateTimeFormatter.format(date).replace(', ', ' ');
}

/** Solo la hora, para timelines donde la fecha ya esta en el encabezado. */
export function formatTime(value: string | null | undefined): string {
  if (!value) {
    return '—';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return timeFormatter.format(date);
}

/** Duracion entre dos instantes ISO, en ms o s segun corresponda. */
export function formatDuration(
  start: string | null | undefined,
  end: string | null | undefined,
): string {
  if (!start || !end) {
    return '—';
  }
  const from = new Date(start).getTime();
  const to = new Date(end).getTime();
  if (Number.isNaN(from) || Number.isNaN(to)) {
    return '—';
  }
  const ms = to - from;
  if (ms < 0) {
    return '—';
  }
  if (ms < 1000) {
    return `${ms} ms`;
  }
  if (ms < 60_000) {
    return `${(ms / 1000).toFixed(1)} s`;
  }
  const minutes = Math.floor(ms / 60_000);
  const seconds = Math.round((ms % 60_000) / 1000);
  return `${minutes} min ${seconds} s`;
}

/** Primeros 8 caracteres de un uuid, para tablas densas. */
export function shortId(value: string): string {
  return value.length <= 10 ? value : `${value.slice(0, 8)}…`;
}

/**
 * JSON legible para los paneles de payload. Si el valor no es serializable
 * (referencias circulares u otra sorpresa) se dice explicitamente en vez de
 * dejar el panel en blanco.
 */
export function prettyJson(value: unknown): string {
  if (value === null || value === undefined) {
    return '';
  }
  if (typeof value === 'string') {
    // Los snapshots a veces vienen como string con JSON adentro; se intenta abrir.
    const trimmed = value.trim();
    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
      try {
        return JSON.stringify(JSON.parse(trimmed), null, 2);
      } catch {
        return value;
      }
    }
    return value;
  }
  try {
    return JSON.stringify(value, null, 2);
  } catch (error) {
    const detail = error instanceof Error ? error.message : String(error);
    return `// No se pudo serializar el payload: ${detail}`;
  }
}

/** Convierte un ISO a lo que espera un `<input type="datetime-local">`. */
export function toDatetimeLocalValue(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  const pad = (n: number): string => String(n).padStart(2, '0');
  return [
    date.getFullYear(),
    '-',
    pad(date.getMonth() + 1),
    '-',
    pad(date.getDate()),
    'T',
    pad(date.getHours()),
    ':',
    pad(date.getMinutes()),
  ].join('');
}

/** Pasa el valor de un `<input type="datetime-local">` a ISO UTC. */
export function datetimeLocalToIso(value: string): string | null {
  if (!value) {
    return null;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return date.toISOString();
}
